// RR Product Recs sagas called in product initialSaga & category initialSaga
import { call, put, take, select, fork, race } from 'redux-saga/effects';
import { debug, error } from '../utils/logger';

import { makeRequest } from './makeRequest';
import { saveUgcPosts, saveUgcTray, UGC_RENDERED } from '../actions/ugcActions';
import { fetchHeartedPics, FETCH_HEARTED_PICS_SUCCESS } from '../actions/engageActions';
import {
    isEngage,
    getPostIds,
    mergeFeaturedPosts,
    buildUgcQuery,
    hasFeaturedFilter,
    convertFeaturedUgcConfig
} from '../utils/ugcUtils';
import { getUgcPostsRoute } from '../utils/apiRoutes';
import { getProductsByArray } from '../../../product/js/sagas/productLookup';
import { applyFavorites } from '../../../product/js/utils/favoritesUtils';
import { updateGridHearts } from '../utils/heartsUtils.js';

/**
 * Makes a call to the UGC service to receive approved Instagram posts
 * @param {array} ugcConfig Configuration for the UGC module
 * @returns {object} response from the UGC API
 */
export function *getPostsSaga(ugcConfig) {
    try {
        const state = yield select();
        const ugcQuery = buildUgcQuery(state, ugcConfig);
        const route = getUgcPostsRoute(state, ugcQuery);
        const res = yield call(makeRequest, route);

        return res;
    } catch (err) {
        error('Unable to get UGC posts');
        return {};
    }
}

/**
 * Gets an initial set of UGC posts and then saves them into the store
 * @param {integer} ugcIndex Index of the UGC module on the page
 * @param {object} ugcConfig Configuration for the UGC module
 */
export function *getAndSaveInitialPostsSaga(ugcIndex, ugcConfig) {
    const ugcPosts = yield call(getPostsSaga, ugcConfig);

    // Save off the configuration used to pull these posts for later use
    ugcPosts.config = _.cloneDeep(ugcConfig);

    if (hasFeaturedFilter(ugcConfig)) {
        // Also pull featured products
        const featuredUgcConfig = convertFeaturedUgcConfig(ugcConfig);
        const ugcFeatured = yield call(getPostsSaga, featuredUgcConfig);
        const mergedUgcProducts = mergeFeaturedPosts(ugcFeatured, ugcPosts);

        yield put(saveUgcPosts(ugcIndex, mergedUgcProducts));
    } else {

        yield put(saveUgcPosts(ugcIndex, ugcPosts));
    }
}

/**
 * Load UGC content for the indexth UGC element.
 * This is the first saga called in the UGC component initialization
 * @param {object} ugcConfig UGC configuration
 * @param {integer} index The current call is for the index'th UGC element on the page.
 */
export function *getUGCPostsSaga(ugcConfigs) {

    for (let i = 0; i < ugcConfigs.length; i++) {
        const ugcConfig = ugcConfigs[i];

        yield fork(getAndSaveInitialPostsSaga, i, ugcConfig);
    }
}

/**
 * @param {integer} ugcIndex Index of the UGC module on the page
 * @param {object} ugcConfig Configuration for the UGC module
 */
export function *getMorePosts(ugcIndex, ugcConfig) {
    const state = yield select();
    const offset = _.get(state, `core.ugc.${ugcIndex}.offset`, 0);
    const bookmark = _.get(state, `core.ugc.${ugcIndex}.bookmark`);

    const configWithPagination = _.merge(_.clone(ugcConfig), {
        bookmark: bookmark,
        offset: offset
    });

    const ugcApiResponse = yield call(getPostsSaga, configWithPagination);

    if (_.get(ugcApiResponse, 'items')) {
        // Prepend the old items to the new items
        const oldItems = _.get(yield select(), `core.ugc.${ugcIndex}.items`);

        Array.prototype.unshift.apply(ugcApiResponse.items, oldItems);
    }

    yield put(saveUgcPosts(ugcIndex, ugcApiResponse));
}

/**
 * Get the products for the tray
 * @param {number} ugcIndex Index of the UGC module on the page
 * @param {array} productIds List of product Ids to pull
 */
export function *getTrayProductsSaga(ugcIndex, productIds, productIdentifier) {
    const state = yield select();

    // clear old products in tray from previous clicks
    yield put(saveUgcTray(ugcIndex, undefined));

    const browseCategory = _.get(state, 'category.categorySlug');
    const linkedProductData = yield call(getProductsByArray, state, productIds, browseCategory, productIdentifier);
    const favoritesApplied = yield call(applyFavorites, state, linkedProductData.products);

    yield put(saveUgcTray(ugcIndex, favoritesApplied));
}

/**
 * Update the hearts on a UGC page
 * @param ugc ugc data instance from the store (core.ugc[x])
 */
export function *updateHearts(ugc) {
    debug('Updating UGC hearts');

    const state = yield select();
    const picIds = getPostIds(ugc.items);
    const heartedPics = new Set(Object.keys(_.get(state, 'core.heartedPics', {})));

    updateGridHearts(picIds, heartedPics);
}

/**
 * Listens for the event signalling that hearted pics were successfully fetched
 *  or that the UGC has finished rendering (apply hearts AFTER divs are rendered
 *  to prevent a race condition)
 */
export function *heartsUpdatedListenerSaga() {
    debug('Saga: heartsUpdatedListenerSaga initialized');

    while (true) {
        yield race({
            fetched: take(FETCH_HEARTED_PICS_SUCCESS),
            rendered: take(UGC_RENDERED)
        });

        debug('Hearted Pics fetched or UGC rendered');

        const state = yield select();
        const ugcs = _.get(state, `core.ugc`, []);

        for (let index = 0; index < ugcs.length; index++) {
            const ugc = ugcs[index];
            const ugcConfig = _.get(ugc, 'config', {});

            if (isEngage(ugcConfig)) {
                yield fork(updateHearts, ugc);
            }
        }
    }
}

export default [heartsUpdatedListenerSaga];
